import { Badge, Box, Button, Card, Flex, Text } from '@chakra-ui/react';
import {
	ConfirmModal,
	createDataCollectionContext,
	dbTimeToUserTime,
	getBadgeColorByStatus,
	SmsMessageRecipientDeliveryStatusGroups,
	useMobileContext,
	type ConfirmModalHandle,
} from '@shared';
import { createColumnHelper } from '@tanstack/react-table';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useMeta } from '../../api/hooks/useMeta';
import { getBadgeColor } from './utils';
import smsMessageRecipientsService from '../../api/services/smsMessageRecipientsService';
import { useDrawer, useGetSingleSmsMessage } from '@admin';
import ContactDrawer from '../contacts/tabs/contacts/drawer/ContactDrawer';

const columnHelper = createColumnHelper<SmsMessageRecipient>();
const TIMEZONE = import.meta.env.VITE_DEFAULT_TIMEZONE as string;

export default function RecipientDetailsContainer({ smsMessageID }: { smsMessageID: number }) {
	const { meta } = useMeta('smsMessagesService');
	const { openDrawer, closeDrawer } = useDrawer();
	const { isMobile } = useMobileContext();

	const { data: message } = useGetSingleSmsMessage(smsMessageID as number, {
		isEnabled: !!smsMessageID,
		getRelated: 'smsMessageGroups',
		scopeWith: 'creditCount|processingCount|deliveredCount|failedCount|expectedRecipientCount|soleRecipient',
		countRelated: 'smsMessageRecipients',
	});

	const { DataCollectionContextProvider, DataCollectionTable, DataCollectionTableFooter, DataCollectionTableHeader } = useMemo(
		() => createDataCollectionContext<SmsMessageRecipient>(),
		[],
	);

	const queryClient = useQueryClient();

	const { t } = useTranslation();

	useEffect(() => {
		queryClient.invalidateQueries({ queryKey: ['smsMessageRecipients'] });
	}, [queryClient]);

	const columns = [
		columnHelper.display({
			header: t('general.contact_name'),
			id: 'userFullName',
			cell: ({ row }) => {
				const firstName = row.original.contact?.firstName;
				const lastName = row.original.contact?.lastName;

				return (
					<Text
						onClick={async e => {
							e.stopPropagation();

							if (row.original.contact === null) {
								await noContactModalRef.current?.open({
									title: t('general.contact_not_found'),
									content: t('general.contact_not_found_explanation'),
									isBackButtonVisible: false,
									confirmButtonText: 'Ok',
									confirmButtonVariant: 'solid',
								});

								return;
							}
							openDrawer({
								id: 'contactDrawer',
								content: <ContactDrawer closeDrawer={() => closeDrawer('contactDrawer')} contactID={row.original?.contactID} />,
							});
						}}
						cursor={'pointer'}
						width="fit-content"
						borderRadius={'8px'}
						padding={1}
						_hover={{ color: 'white', backgroundColor: 'rgb(61, 213, 152)' }}
					>
						{firstName} {lastName}
					</Text>
				);
			},
		}),
		columnHelper.accessor('phone', {
			header: t('general.phone'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return (
					<Text
						width="fit-content"
						borderRadius={'8px'}
						padding={1}
						_hover={{ color: 'white', backgroundColor: 'rgb(61, 213, 152)' }}
						onClick={async e => {
							e.stopPropagation();

							if (row.original.contact === null) {
								await noContactModalRef.current?.open({
									title: t('general.contact_not_found'),
									content: t('general.contact_not_found_explanation'),
									isBackButtonVisible: false,
									confirmButtonText: 'Ok',
									confirmButtonVariant: 'solid',
								});

								return;
							}

							openDrawer({
								id: 'contactDrawer',
								content: <ContactDrawer closeDrawer={() => closeDrawer('contactDrawer')} contactID={row.original?.contactID} />,
							});
						}}
						cursor={'pointer'}
					>
						{row.original.phone}
					</Text>
				);
			},
		}),

		columnHelper.accessor('sendingStatus', {
			header: t('general.sending_status'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return <Badge colorPalette={getBadgeColorByStatus(row.original.sendingStatus)}>{meta?.smsMessageRecipientSendingStatus?.[row.original.sendingStatus]}</Badge>;
			},
		}),
		columnHelper.accessor('timeSent', {
			header: t('general.time_sent'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				if (row.original.timeSent) {
					return <Text>{dbTimeToUserTime(row.original.timeSent, TIMEZONE, { includeSeconds: true })}</Text>;
				}
				return t('general.not_sent');
			},
		}),
		columnHelper.accessor('timeDelivered', {
			header: t('general.time_delivered'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				if (row.original.timeDelivered) {
					return <Text>{dbTimeToUserTime(row.original.timeDelivered, TIMEZONE, { includeSeconds: true })}</Text>;
				}
				return <Badge colorPalette="red">{t('general.not_delivered')}</Badge>;
			},
		}),
		columnHelper.accessor('deliveryStatusCode', {
			header: t('general.information'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				const statusCode = row.original.deliveryStatusCode;
				if (!statusCode) {
					return null;
				}

				console.log(row.original);

				const statusInfo = meta?.smsMessageRecipientDeliveryStatusCode?.[statusCode];

				if (!meta) {
					return <Badge colorPalette={'grey'}>{`${row.original.deliveryStatusCode} - ${statusInfo}`}</Badge>;
				}

				return <Badge colorPalette={getBadgeColor(meta, statusCode.toString())}>{`${row.original.deliveryStatusCode} - ${statusInfo}`}</Badge>;
			},
			filterFn: 'arrIncludesSomeSensitive',

			meta: {
				filterLabel: t('general.status'),
				filterVariant: 'select',
				filterOptions: [
					{
						value: SmsMessageRecipientDeliveryStatusGroups.delivered.join(','),
						label: t('general.delivered') ?? 'Delivered',
					},
					{
						value: SmsMessageRecipientDeliveryStatusGroups.processing.join(','),
						label: t('general.processing') ?? 'Processing',
					},
					{
						value: SmsMessageRecipientDeliveryStatusGroups.failed.join(','),
						label: t('general.failed') ?? 'Failed',
					},
				],
			},
		}),
		columnHelper.accessor('statusMessage', {
			header: t('general.status_message'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('timeModified', {
			header: t('general.time_modified'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				if (row.original.timeModified) {
					return <Text>{dbTimeToUserTime(row.original.timeModified, TIMEZONE, { includeSeconds: true })}</Text>;
				}
				return t('general.not_sent');
			},
		}),
		columnHelper.display({
			id: 'contactID',
			cell: ({ row }) => {
				return (
					<Button
						display={'block'}
						ml="auto"
						onClick={() =>
							openDrawer({
								id: 'contactDrawer',
								content: <ContactDrawer closeDrawer={() => closeDrawer('contactDrawer')} contactID={row.original?.contactID} />,
							})
						}
						size="xs"
					>
						{t('general.open_contact')}
					</Button>
				);
			},
		}),
	];

	const noContactModalRef = useRef<ConfirmModalHandle>(null);

	return (
		<Card.Root flex={1} display="flex">
			<Card.Body display="flex" flexDirection="column" padding={5} gap={5} overflow={isMobile ? 'initial' : 'auto'}>
				{message?.sendingStatus === 'scheduled' ? (
					<ScheduledRecipientsList smsMessageID={smsMessageID} />
				) : (
					<DataCollectionContextProvider
						dataKey={`smsMessageRecipients`}
						keyAttribute="smsMessageRecipientID"
						emptyDataText={t('general.no_recipients_found')}
						fetchFn={async (...args) => smsMessageRecipientsService.getAllBySmsMessageId(smsMessageID, ...args)}
						queryParams={{
							getRelated: 'contact',
						}}
						initialItemsPerPage={25}
						columns={columns}
						defaultHiddenColumns={['statusMessage']}
						isServerSide={true}
						isSelectable={false}
						defaultSort={{ id: 'timeDelivered', desc: true }}
					>
						<Flex gap={2} flexWrap="wrap" flexShrink={0}>
							<DataCollectionTableHeader />
						</Flex>
						<Box flex={1} overflow="auto" minH={0}>
							<DataCollectionTable />
						</Box>
						<Flex gap={2} flexShrink={0}>
							<DataCollectionTableFooter />
						</Flex>
						<ConfirmModal ref={noContactModalRef} />
					</DataCollectionContextProvider>
				)}
			</Card.Body>
		</Card.Root>
	);
}

const columnHelperContacts = createColumnHelper<Contact>();

function ScheduledRecipientsList({ smsMessageID }: { smsMessageID: number }) {
	const { t } = useTranslation();
	const { DataCollectionContextProvider, DataCollectionTable, DataCollectionTableFooter, DataCollectionTableHeader } = useMemo(() => createDataCollectionContext<Contact>(), []);
	const { openDrawer, closeDrawer } = useDrawer();

	const columns = [
		columnHelperContacts.accessor('phone', {
			header: t('general.phone'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return (
					<Text
						width="fit-content"
						borderRadius={'8px'}
						padding={1}
						_hover={{ color: 'white', backgroundColor: 'rgb(61, 213, 152)' }}
						onClick={e => {
							e.stopPropagation();
							openDrawer({
								id: 'contactDrawer',
								content: <ContactDrawer closeDrawer={() => closeDrawer('contactDrawer')} contactID={row.original?.contactID} />,
							});
						}}
						cursor={'pointer'}
					>
						{row.original.phone}
					</Text>
				);
			},
		}),
		columnHelperContacts.display({
			id: 'contactID',
			cell: ({ row }) => {
				return (
					<Button
						display={'block'}
						ml="auto"
						onClick={() =>
							openDrawer({
								id: 'contactDrawer',
								content: <ContactDrawer closeDrawer={() => closeDrawer('contactDrawer')} contactID={row.original?.contactID} />,
							})
						}
						size="xs"
					>
						{t('general.open_contact')}
					</Button>
				);
			},
		}),
	];

	return (
		<DataCollectionContextProvider
			dataKey={`contacts`}
			keyAttribute="contactID"
			emptyDataText={t('general.no_recipients_found')}
			fetchFn={async () => smsMessageRecipientsService.getAllExpectedBySmsMessageId(smsMessageID)}
			initialItemsPerPage={25}
			columns={columns}
			isServerSide={true}
			isSelectable={false}
			defaultSort={{ id: 'phone', desc: true }}
		>
			<Flex gap={2} flexWrap="wrap" flexShrink={0} ml="auto">
				<DataCollectionTableHeader />
			</Flex>
			<Box flex={1} overflow="auto" minH={0}>
				<DataCollectionTable />
			</Box>
			<Flex gap={2} flexShrink={0}>
				<DataCollectionTableFooter />
			</Flex>
		</DataCollectionContextProvider>
	);
}
